import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { STATION, NOW_PLAYING } from "../mock";

const PlayerContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// The radio server is reachable directly over HTTPS, so the browser holds ONE
// persistent connection straight to it — exactly how normal web radio works.
// This removes the periodic stream-swap that used to cause tiny hiccups/skips.
// If the direct connection ever fails we automatically fall back to the backend
// proxy (which reconnects upstream internally).
const DIRECT_SRC = process.env.REACT_APP_STREAM_URL || "https://media2.hostin.cc/allinradio.mp3";
const PROXY_SRC = `${API}/stream`;
const SOURCES = [DIRECT_SRC, PROXY_SRC];

export const usePlayer = () => {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error("usePlayer must be used within PlayerProvider");
  return ctx;
};

export const PlayerProvider = ({ children }) => {
  const audioRef = useRef(null);
  const shouldPlayRef = useRef(false);   // user intent
  const srcIndexRef = useRef(0);         // which source we're currently on
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const analyserRef = useRef(null);      // kept for API compatibility (unused)

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [volume, setVolumeState] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [currentTitle, setCurrentTitle] = useState(NOW_PLAYING.title);
  const [currentArtwork, setCurrentArtwork] = useState(null);

  // Keep the element in sync with volume/mute changes.
  useEffect(() => {
    const el = audioRef.current;
    if (el) { el.muted = isMuted; el.volume = volume; }
  }, [volume, isMuted]);

  // Poll the backend for the live "now playing" track title + artwork.
  useEffect(() => {
    let active = true;
    const fetchNowPlaying = async () => {
      try {
        const res = await fetch(`${API}/now-playing`);
        if (!res.ok) return;
        const data = await res.json();
        if (active && data && data.title) {
          setCurrentTitle(data.title);
          setCurrentArtwork(data.artwork || null);
        }
      } catch (e) { /* ignore transient errors */ }
    };
    fetchNowPlaying();
    const id = setInterval(fetchNowPlaying, 5000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const clearReconnect = useCallback(() => {
    if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
  }, []);

  const setMediaSessionMetadata = useCallback(() => {
    if (!("mediaSession" in navigator) || !window.MediaMetadata) return;
    try {
      const art = `${window.location.origin}/allin-media-artwork.png`;
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: "All-In Radio",
        artist: "Live · 24/7 online radio",
        artwork: [
          { src: art, sizes: "512x512", type: "image/png" },
          { src: art, sizes: "256x256", type: "image/png" },
          { src: art, sizes: "192x192", type: "image/png" },
        ],
      });
    } catch (e) { /* not supported */ }
  }, []);

  // (Re)start playback on the current source. Cache-busts so the browser always
  // opens a fresh live connection instead of resuming a stale buffered one.
  const startCurrentSource = useCallback(async () => {
    const el = audioRef.current;
    if (!el) return;
    const base = SOURCES[srcIndexRef.current];
    const sep = base.includes("?") ? "&" : "?";
    el.src = `${base}${sep}_t=${Date.now()}`;
    el.muted = isMuted;
    el.volume = volume;
    el.load();
    await el.play();
  }, [isMuted, volume]);

  const play = useCallback(async () => {
    const el = audioRef.current;
    if (!el) return;
    shouldPlayRef.current = true;
    reconnectAttemptRef.current = 0;
    srcIndexRef.current = 0; // always try the direct HTTPS source first
    try {
      setError(null);
      setIsLoading(true);
      clearReconnect();
      await startCurrentSource();
      setIsPlaying(true);
      setHasStarted(true);
      setMediaSessionMetadata();
    } catch (e) {
      // If the direct source is blocked, immediately try the proxy fallback.
      if (srcIndexRef.current < SOURCES.length - 1) {
        srcIndexRef.current += 1;
        try {
          await startCurrentSource();
          setIsPlaying(true);
          setHasStarted(true);
          setMediaSessionMetadata();
          setIsLoading(false);
          return;
        } catch (e2) { /* fall through to error */ }
      }
      setError("De livestream kon niet worden gestart. Probeer het opnieuw.");
      setIsPlaying(false);
    } finally {
      setIsLoading(false);
    }
  }, [startCurrentSource, setMediaSessionMetadata, clearReconnect]);

  const pause = useCallback(() => {
    shouldPlayRef.current = false;
    clearReconnect();
    const el = audioRef.current;
    if (el) { try { el.pause(); } catch (e) {} }
    setIsPlaying(false);
    setIsLoading(false);
  }, [clearReconnect]);

  // Automatic recovery if the live connection drops unexpectedly.
  const handleFailure = useCallback(() => {
    if (!shouldPlayRef.current || reconnectTimerRef.current) return;
    setIsLoading(true);
    reconnectAttemptRef.current += 1;
    // After a couple of failed attempts on one source, switch to the other.
    if (reconnectAttemptRef.current % 3 === 0 && SOURCES.length > 1) {
      srcIndexRef.current = (srcIndexRef.current + 1) % SOURCES.length;
    }
    const delay = Math.min(3000, 500 * reconnectAttemptRef.current);
    reconnectTimerRef.current = setTimeout(async () => {
      reconnectTimerRef.current = null;
      if (!shouldPlayRef.current) return;
      try {
        await startCurrentSource();
      } catch (e) {
        handleFailure();
      }
    }, delay);
  }, [startCurrentSource]);

  const togglePlay = useCallback(() => { if (isPlaying) pause(); else play(); }, [isPlaying, play, pause]);
  const setVolume = useCallback((v) => { setVolumeState(v); if (v > 0) setIsMuted(false); }, []);
  const toggleMute = useCallback(() => setIsMuted((m) => !m), []);

  useEffect(() => () => clearReconnect(), [clearReconnect]);

  const value = {
    isPlaying,
    isLoading,
    volume,
    isMuted,
    error,
    hasStarted,
    togglePlay,
    play,
    pause,
    setVolume,
    toggleMute,
    station: STATION,
    nowPlaying: { title: currentTitle || NOW_PLAYING.title, artist: NOW_PLAYING.artist, artwork: currentArtwork },
    analyserRef,
  };

  const handlers = {
    onPlaying: () => { setIsLoading(false); setIsPlaying(true); reconnectAttemptRef.current = 0; },
    onWaiting: () => { if (shouldPlayRef.current) setIsLoading(true); },
    onStalled: () => { if (shouldPlayRef.current) setIsLoading(true); },
    onEnded: () => handleFailure(),
    onError: () => {
      if (shouldPlayRef.current) handleFailure();
      else { if (hasStarted) setError("Verbinding met de stream verbroken."); setIsPlaying(false); setIsLoading(false); }
    },
    onPause: () => { if (shouldPlayRef.current) handleFailure(); },
  };

  return (
    <PlayerContext.Provider value={value}>
      {children}
      <audio ref={audioRef} preload="none" {...handlers} />
    </PlayerContext.Provider>
  );
};

export default PlayerContext;
